import { computed, type MaybeRef, unref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type {
  AdminProjectDetail,
  AdminStoreProjectParticipantRequest,
  ProjectStatus,
  QuotePaymentStatus,
  QuoteStatus,
} from "@shopexperts/legacy-api";

export function useAdminProjectDetail(projectId: MaybeRef<number | null | undefined>) {
  const api = useApi();
  const queryClient = useQueryClient();
  const resolvedProjectId = computed(() => unref(projectId) ?? 0);

  const detailQuery = useQuery({
    queryKey: computed(() => ["admin", "projects-v2", "detail", resolvedProjectId.value]),
    queryFn: async (): Promise<AdminProjectDetail> => {
      return await api.admin.projectsV2.getProject({
        params: { projectId: resolvedProjectId.value },
      }) as AdminProjectDetail;
    },
    enabled: computed(() => resolvedProjectId.value > 0),
    staleTime: 10_000,
  });

  async function invalidateRelated() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["admin", "projects-v2", "detail", resolvedProjectId.value],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "leads-projects", "queue"],
      }),
    ]);
  }

  const updateProjectStatus = useMutation({
    mutationFn: async (projectStatus: ProjectStatus) => {
      return await api.admin.projectsV2.updateStatus({
        params: { projectId: resolvedProjectId.value },
        body: { project_status: projectStatus },
      });
    },
    onSuccess: invalidateRelated,
  });

  const updateQuoteStatus = useMutation({
    mutationFn: async (input: { quoteId: number; status: QuoteStatus }) => {
      return await api.admin.projectsV2.updateQuoteStatus({
        params: { projectId: resolvedProjectId.value, quoteId: input.quoteId },
        body: { status: input.status },
      });
    },
    onSuccess: invalidateRelated,
  });

  const updateQuotePaymentStatus = useMutation({
    mutationFn: async (input: { quoteId: number; paymentStatus: QuotePaymentStatus | null }) => {
      return await api.admin.projectsV2.updateQuotePaymentStatus({
        params: { projectId: resolvedProjectId.value, quoteId: input.quoteId },
        body: { payment_status: input.paymentStatus },
      });
    },
    onSuccess: invalidateRelated,
  });

  const addParticipant = useMutation({
    mutationFn: async (body: AdminStoreProjectParticipantRequest) => {
      return await api.admin.projectsV2.addParticipant({
        params: { projectId: resolvedProjectId.value },
        body,
      });
    },
    onSuccess: invalidateRelated,
  });

  const removeParticipant = useMutation({
    mutationFn: async (participantId: number) => {
      return await api.admin.projectsV2.removeParticipant({
        params: { projectId: resolvedProjectId.value, participantId },
      });
    },
    onSuccess: invalidateRelated,
  });

  const unmatchProject = useMutation({
    mutationFn: async () => {
      return await api.admin.projectsV2.unmatch({
        params: { projectId: resolvedProjectId.value },
      });
    },
    onSuccess: invalidateRelated,
  });

  return {
    project: computed(() => detailQuery.data.value ?? null),
    isLoading: detailQuery.isLoading,
    error: detailQuery.error,
    refetch: detailQuery.refetch,
    updateProjectStatus,
    updateQuoteStatus,
    updateQuotePaymentStatus,
    addParticipant,
    removeParticipant,
    unmatchProject,
  };
}
